import React, { useEffect, useState } from "react";
import { useProperty } from "@/context/PropertyContext";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Save, Info } from "lucide-react";
import { toast } from "sonner";

const PLACEHOLDERS = [
  ["_ARRENDATARIO_", "Nombre completo"],
  ["_DNI_", "Nº documento (DNI / Pasaporte / Permiso de trabajo)"],
  ["_NACIONALIDAD_", "Nacionalidad"],
  ["_DIRECCION_", "Dirección"],
  ["_HAB_", "Nº habitación"],
  ["_FECHA_ENTRADA_", "Fecha inicio"],
  ["_FECHA_SALIDA_", "Fecha fin"],
  ["_DURACION_", "Meses"],
  ["_FECHA_ANTIGUEDAD_", "Inicio del primer contrato del inquilino en el piso"],
  ["_CUOTA_", "Renta (sin gastos, en letra)"],
  ["_GASTOS_", "Gastos (en letra)"],
  ["_CUO_GAS_", "Renta + gastos (en letra)"],
  ["_FIANZA_", "Fianza (en letra)"],
  ["_LLAVE_", "Coste llave (en letra)"],
];

const PLACEHOLDERS_RESERVA = [
  ["_ARRENDATARIO_", "Nombre completo"],
  ["_DNI_", "Nº documento (DNI / Pasaporte / Permiso de trabajo)"],
  ["_NACIONALIDAD_", "Nacionalidad"],
  ["_DIRECCION_", "Dirección del inquilino"],
  ["_HAB_", "Nº habitación"],
  ["_IMPORTE_RESERVA_", "Importe de la reserva (en letra)"],
  ["_FECHA_ENTRADA_", "Fecha de entrada prevista"],
  ["_DURACION_", "Meses"],
  ["_CUO_GAS_", "Mensualidad con gastos (en letra)"],
  ["_FECHA_LIMITE_", "Fecha límite para firmar el contrato"],
  ["_FECHA_RESERVA_", "Fecha del documento"],
  ["_DIR_PISO_", "Dirección del piso"],
  ["_IBAN_", "IBAN del piso"],
  ["_DNI_ARRENDADOR_", "DNI del arrendador"],
  ["_ARRENDADOR_", "Nombre del arrendador"],
];

export default function Plantilla() {
  const { selectedId, selected, reload } = useProperty();
  const [tipo, setTipo] = useState("contrato"); // contrato | reserva
  const [texto, setTexto] = useState("");
  const [reservaPorDefecto, setReservaPorDefecto] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (tipo === "contrato") {
      if (selected) setTexto(selected.plantilla_contrato || "");
    } else if (selectedId) {
      api.get(`/properties/${selectedId}/plantilla-reserva`).then(({ data }) => {
        setTexto(data.plantilla_reserva || "");
        setReservaPorDefecto(data.por_defecto);
      }).catch(() => toast.error("Error al cargar la plantilla de reserva"));
    }
  }, [selected, selectedId, tipo]);

  const save = async () => {
    setSaving(true);
    try {
      const campo = tipo === "contrato" ? "plantilla_contrato" : "plantilla_reserva";
      await api.patch(`/properties/${selectedId}/plantilla`, { [campo]: texto });
      toast.success("Plantilla guardada");
      if (tipo === "reserva") setReservaPorDefecto(false);
      reload();
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!selectedId)
    return <div className="data-card p-10 text-center text-muted-foreground">Selecciona o crea un piso primero.</div>;

  const placeholders = tipo === "contrato" ? PLACEHOLDERS : PLACEHOLDERS_RESERVA;

  return (
    <div data-testid="plantilla-page">
      <PageHeader title={tipo === "contrato" ? "Plantilla de Contrato" : "Plantilla de Reserva"} subtitle={selected?.nombre}>
        <div className="flex rounded-md border border-border overflow-hidden">
          {[["contrato", "Contrato"], ["reserva", "Reserva"]].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTipo(k)}
              className={`px-3 py-1.5 text-sm ${tipo === k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
              data-testid={`plantilla-tab-${k}`}
            >
              {label}
            </button>
          ))}
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-plantilla-button">
          <Save className="h-4 w-4 mr-1.5" /> {saving ? "Guardando…" : "Guardar plantilla"}
        </Button>
      </PageHeader>

      {tipo === "reserva" && reservaPorDefecto && (
        <p className="text-xs text-muted-foreground mb-3">
          Este piso aún no tiene plantilla de reserva propia: se muestra la plantilla base. Al guardar quedará como la de este piso.
        </p>
      )}

      <div className="grid lg:grid-cols-4 gap-4">
        <div className="lg:col-span-3">
          <Textarea
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            className="font-mono text-sm min-h-[600px] leading-relaxed"
            data-testid="plantilla-textarea"
            placeholder="Escribe aquí el texto del contrato usando los marcadores de la derecha…"
          />
        </div>
        <div className="lg:col-span-1">
          <div className="data-card p-4 sticky top-20">
            <div className="flex items-center gap-2 mb-3">
              <Info className="h-4 w-4 text-primary" />
              <p className="overline">Marcadores</p>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              Estos textos se sustituyen automáticamente al generar cada {tipo === "contrato" ? "contrato" : "resguardo"}. Haz clic para copiar.
            </p>
            <div className="space-y-1.5">
              {placeholders.map(([code, desc]) => (
                <button
                  key={code}
                  onClick={() => { navigator.clipboard.writeText(code); toast.success(`Copiado: ${code}`); }}
                  className="w-full text-left rounded-md border border-border p-2 hover:border-primary transition-colors"
                  data-testid={`placeholder-${code}`}
                >
                  <code className="text-xs font-mono text-primary block">{code}</code>
                  <span className="text-[11px] text-muted-foreground">{desc}</span>
                </button>
              ))}
            </div>
            {tipo === "contrato" && (
              <>
                <p className="overline mt-5 mb-2">Bloques condicionales</p>
                <p className="text-xs text-muted-foreground mb-2">
                  Texto que solo aparece según el contrato tenga marcado "Renovación" o no. Cada marca en su propia línea.
                </p>
                <div className="space-y-1.5">
                  {[
                    ["[[NUEVO]] … [[/NUEVO]]", "Solo en contratos nuevos"],
                    ["[[RENOVACION]] … [[/RENOVACION]]", "Solo en renovaciones"],
                  ].map(([code, desc]) => (
                    <div key={code} className="rounded-md border border-border p-2">
                      <code className="text-xs font-mono text-primary block">{code}</code>
                      <span className="text-[11px] text-muted-foreground">{desc}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
